import React from "react";

import { HappyHourOffersBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ANTONIO_URL, TYPEKIT_URL } from "../../utils/groups";

import hh from "../../styles/happy-hour.module.scss";
import styles from "./index.module.scss";

interface Props {
    value: Value;
}

export const HappyHourOffersBlock = (props: Props) => {
    const value = props.value;
    if (!value.heading) {
        return null;
    }
    const hours = (value.hours ?? []).filter((line) => Boolean(line));
    const offers = (value.offers ?? []).filter(
        (offer) => offer.image && offer.title,
    );
    const feature = value.feature_image;
    return (
        <section
            className={concatClassNames([
                hh.tokens,
                hh.page,
                styles.section,
                value.compact_top ? styles.compactTop : undefined,
            ])}
        >
            <link rel="stylesheet" href={TYPEKIT_URL} precedence="default" />
            <link rel="stylesheet" href={ANTONIO_URL} precedence="default" />
            <div className={hh.container}>
                <h2 className={hh.title}>{value.heading}</h2>
                {hours.length > 0 && (
                    <p className={styles.hours}>
                        {hours.map((line, index) => (
                            <span key={index} className={styles.hoursLine}>
                                {line}
                            </span>
                        ))}
                    </p>
                )}
                {offers.length > 0 && (
                    <div className={styles.offers}>
                        {offers.map((offer, index) => (
                            <article key={index} className={styles.offer}>
                                {offer.image && (
                                    <img
                                        className={styles.offerPhoto}
                                        src={offer.image.url}
                                        alt={offer.image.default_alt_text}
                                        width={offer.image.width}
                                        height={offer.image.height}
                                        loading="lazy"
                                    />
                                )}
                                <div className={styles.offerBody}>
                                    <h3 className={styles.offerTitle}>
                                        {offer.title}
                                    </h3>
                                    {offer.items && offer.items.length > 0 && (
                                        <ul className={styles.offerList}>
                                            {offer.items.map((item, i) => (
                                                <li key={i}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                )}
                {feature && (
                    <article className={styles.feature}>
                        <img
                            className={styles.featurePhoto}
                            src={feature.url}
                            alt={feature.default_alt_text}
                            width={feature.width}
                            height={feature.height}
                            loading="lazy"
                        />
                        <div className={styles.featurePanel}>
                            {value.feature_script && (
                                <p
                                    className={concatClassNames([
                                        hh.script,
                                        styles.featureScript,
                                    ])}
                                >
                                    {value.feature_script}
                                </p>
                            )}
                            {value.feature_eyebrow && (
                                <p className={styles.featureEyebrow}>
                                    {value.feature_eyebrow}
                                </p>
                            )}
                            <div aria-hidden className={styles.featureRule} />
                            {value.feature_title && (
                                <h3 className={styles.featureTitle}>
                                    {value.feature_title}
                                </h3>
                            )}
                            {value.feature_body && (
                                <p className={styles.featureBody}>
                                    {value.feature_body}
                                </p>
                            )}
                        </div>
                    </article>
                )}
                {value.legal && (
                    <RichTextBlock
                        className={styles.legal}
                        value={value.legal}
                    />
                )}
            </div>
        </section>
    );
};
